import { groupTeamByLocalTime, localTimePeriod, shouldUseListLayout } from "./lib/team_local_time";

function closeAllDropdowns(except = null) {
  document.querySelectorAll("[data-pp-dropdown]").forEach((dropdown) => {
    if (except && dropdown === except) return;
    const button = dropdown.querySelector("[data-pp-dropdown-button]");
    const menu = dropdown.querySelector("[data-pp-dropdown-menu]");
    if (!button || !menu) return;
    menu.classList.add("hidden");
    button.setAttribute("aria-expanded", "false");
  });
}

const THEME_STORAGE_KEY = "pepper:theme";

function systemTheme() {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolvedTheme() {
  return document.documentElement.dataset.theme || systemTheme();
}

function applyThemePreference(preference) {
  try {
    if (preference === "dark" || preference === "light") {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
      document.documentElement.dataset.theme = preference;
    } else {
      localStorage.removeItem(THEME_STORAGE_KEY);
      document.documentElement.removeAttribute("data-theme");
    }
  } catch {
    if (preference === "dark" || preference === "light") {
      document.documentElement.dataset.theme = preference;
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
  }

  const current = resolvedTheme();
  document.querySelectorAll("[data-pp-theme-current]").forEach((el) => {
    el.textContent = current === "dark" ? "Dark" : "Light";
  });

  const pref = preference === "dark" || preference === "light" ? preference : "system";
  document.querySelectorAll("[data-pp-set-theme]").forEach((btn) => {
    const isActive = btn.dataset.ppSetTheme === pref;
    btn.classList.toggle("pp-theme-seg-active", isActive);
    btn.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
}

let sidebarPreviousFocus = null;

function setSidebar(open) {
  const sidebar = document.querySelector("[data-pp-sidebar]");
  if (!sidebar) return;
  const backdrop = document.querySelector("[data-pp-sidebar-backdrop]");
  const mobile = window.matchMedia("(max-width: 1535px)").matches;
  const wasOpen = mobile && !sidebar.classList.contains("-translate-x-full");
  const visible = !mobile || open;
  if (mobile && open && !wasOpen) sidebarPreviousFocus = document.activeElement;
  sidebar.classList.toggle("-translate-x-full", !open);
  sidebar.setAttribute("aria-hidden", visible ? "false" : "true");
  sidebar.inert = !visible;
  if (backdrop) backdrop.classList.toggle("hidden", !mobile || !open);
  document.querySelectorAll("[data-pp-sidebar-toggle]").forEach((btn) => {
    btn.setAttribute("aria-expanded", open ? "true" : "false");
  });
  if (mobile && open) {
    sidebar.querySelector("[data-pp-sidebar-toggle]")?.focus();
  } else if (mobile && wasOpen && sidebarPreviousFocus && typeof sidebarPreviousFocus.focus === "function") {
    sidebarPreviousFocus.focus();
    sidebarPreviousFocus = null;
  }
}

function sidebarIsOpen() {
  const sidebar = document.querySelector("[data-pp-sidebar]");
  return !!sidebar && !sidebar.classList.contains("-translate-x-full");
}

function focusableElements(container) {
  return Array.from(container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
    .filter((element) => !element.hidden && !element.closest("[inert]") && element.getClientRects().length > 0);
}

function trapFocus(container, event) {
  if (event.key !== "Tab") return false;
  const focusable = focusableElements(container);
  if (!focusable.length) {
    event.preventDefault();
    container.focus();
    return true;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) {
    event.preventDefault();
    first.focus();
  }
  return true;
}

function initThemeFromStorage() {
  let storedTheme = null;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark" || stored === "light") {
      storedTheme = stored;
      document.documentElement.dataset.theme = storedTheme;
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
  } catch {
    document.documentElement.removeAttribute("data-theme");
  }

  applyThemePreference(storedTheme || "system");

  if (window.matchMedia) {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if (!document.documentElement.dataset.theme) {
        const current = resolvedTheme();
        document.querySelectorAll("[data-pp-theme-current]").forEach((el) => {
          el.textContent = current === "dark" ? "Dark" : "Light";
        });
      }
    };
    if (media.addEventListener) media.addEventListener("change", handler);
    else if (media.addListener) media.addListener(handler);
  }
}

function dismissToast(toast) {
  if (!toast || toast.dataset.ppToastDismissed) return;
  toast.dataset.ppToastDismissed = "true";
  toast.classList.add("opacity-0", "translate-y-2");
  window.setTimeout(() => toast.remove(), 300);
}

function initToasts() {
  document.querySelectorAll("[data-pp-toast]").forEach((toast) => {
    if (toast.dataset.ppToastBound) return;
    toast.dataset.ppToastBound = "true";

    toast.classList.add("opacity-0", "translate-y-2");
    requestAnimationFrame(() => {
      toast.classList.remove("opacity-0", "translate-y-2");
    });

    const timeoutMs = Number.parseInt(toast.dataset.ppToastTimeout || "6000", 10);
    if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
      window.setTimeout(() => dismissToast(toast), timeoutMs);
    }
  });
}

function toggleDropdown(dropdown) {
  const button = dropdown.querySelector("[data-pp-dropdown-button]");
  const menu = dropdown.querySelector("[data-pp-dropdown-menu]");
  if (!button || !menu) return;

  const isOpen = !menu.classList.contains("hidden");
  closeAllDropdowns(dropdown);

  if (isOpen) {
    menu.classList.add("hidden");
    button.setAttribute("aria-expanded", "false");
  } else {
    menu.classList.remove("hidden");
    button.setAttribute("aria-expanded", "true");
  }
}

function toggleMobileNav(button) {
  const panelId = button.getAttribute("aria-controls");
  if (!panelId) return;
  const panel = document.getElementById(panelId);
  if (!panel) return;

  const isOpen = !panel.classList.contains("hidden");
  if (isOpen) {
    panel.classList.add("hidden");
    button.setAttribute("aria-expanded", "false");
  } else {
    panel.classList.remove("hidden");
    button.setAttribute("aria-expanded", "true");
  }
}

/* ---- Team time-off calendar --------------------------------------------- */
// Tone -> utility classes (kept as literal strings so Tailwind's @source scan
// of this file keeps them in the build).
const CAL_TONES = {
  cayenne: "bg-cayenne/15 text-cayenne border-cayenne/30",
  habanero: "bg-habanero/15 text-habanero border-habanero/30",
  jalapeno: "bg-jalapeno/15 text-jalapeno border-jalapeno/30",
  sriracha: "bg-sriracha/15 text-sriracha border-sriracha/30",
  cinnamon: "bg-cinnamon/15 text-cinnamon border-cinnamon/30",
  ember: "bg-ember/15 text-ember border-ember/30",
};
const CAL_DOTS = {
  cayenne: "bg-cayenne",
  habanero: "bg-habanero",
  jalapeno: "bg-jalapeno",
  sriracha: "bg-sriracha",
  cinnamon: "bg-cinnamon",
  ember: "bg-ember",
};
const CAL_HEADER_H = 22;
const CAL_BAR_H = 18;
const CAL_BAR_GAP = 3;
const CAL_MIN_LANES = 2;

// Country flag artwork (optimized SVG bodies, viewBox 0 0 640 480) for the
// team's home countries. Rendered full-bleed behind holiday days.
const FLAG_INNER = {
  ca: '<path fill="#fff" d="M150.1 0h339.7v480H150z"/><path fill="#d52b1e" d="M-19.7 0h169.8v480H-19.7zm509.5 0h169.8v480H489.9zM201 232l-13.3 4.4 61.4 54c4.7 13.7-1.6 17.8-5.6 25l66.6-8.4-1.6 67 13.9-.3-3.1-66.6 66.7 8c-4.1-8.7-7.8-13.3-4-27.2l61.3-51-10.7-4c-8.8-6.8 3.8-32.6 5.6-48.9 0 0-35.7 12.3-38 5.8l-9.2-17.5-32.6 35.8c-3.5.9-5-.5-5.9-3.5l15-74.8-23.8 13.4c-2 .9-4 .1-5.2-2.2l-23-46-23.6 47.8c-1.8 1.7-3.6 1.9-5 .7L264 130.8l13.7 74.1c-1.1 3-3.7 3.8-6.7 2.2l-31.2-35.3c-4 6.5-6.8 17.1-12.2 19.5-5.4 2.3-23.5-4.5-35.6-7 4.2 14.8 17 39.6 9 47.7"/>',
  gb: '<path fill="#012169" d="M0 0h640v480H0z"/><path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0z"/><path fill="#C8102E" d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z"/><path fill="#FFF" d="M241 0v480h160V0zM0 160v160h640V160z"/><path fill="#C8102E" d="M0 193v96h640v-96zM273 0v480h96V0z"/>',
  ru: '<path fill="#fff" d="M0 0h640v160H0z"/><path fill="#0039a6" d="M0 160h640v160H0z"/><path fill="#d52b1e" d="M0 320h640v160H0z"/>',
  th: '<g fill-rule="evenodd"><path fill="#f4f5f8" d="M0 0h640v480H0z"/><path fill="#2d2a4a" d="M0 162.5h640v160H0z"/><path fill="#a51931" d="M0 0h640v82.5H0zm0 400h640v80H0z"/></g>',
  vn: '<g fill-rule="evenodd" transform="translate(80)scale(.9375)"><path fill="#da251d" d="M-128 0h768v512h-768z"/><path fill="#ff0" d="M349.6 381 260 314.3l-89 67.3L204 272l-89-67.7 110.1-1 34.2-109.4L294 203l110.1.1-88.5 68.4 33.9 109.6z"/></g>',
  us: '<path fill="#fff" d="M0 0h640v480H0z"/><g fill="#b22234"><path d="M0 0h640v37H0z"/><path d="M0 74h640v37H0z"/><path d="M0 148h640v37H0z"/><path d="M0 222h640v37H0z"/><path d="M0 295h640v37H0z"/><path d="M0 369h640v37H0z"/><path d="M0 443h640v37H0z"/></g><path fill="#3c3b6e" d="M0 0h256v259H0z"/>',
};

function calFlagSvg(cc, fit) {
  const inner = FLAG_INNER[cc];
  if (!inner) return "";
  return `<svg viewBox="0 0 640 480" preserveAspectRatio="${fit}" class="h-full w-full" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`;
}

// Flag as a CSS background data-URI, for the repeated bottom-third ribbon.
// Single-quoted url() so it can live inside a double-quoted style attribute.
function calFlagDataUri(cc) {
  const inner = FLAG_INNER[cc];
  if (!inner) return "none";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480">${inner}</svg>`;
  return `url('data:image/svg+xml,${encodeURIComponent(svg).replace(/'/g, "%27")}')`;
}

function calParseISO(s) {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
}
function calAddDays(date, n) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}
function calDiffDays(a, b) {
  return Math.round((b - a) / 86400000);
}
function calSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function calMondayIndex(date) {
  return (date.getDay() + 6) % 7;
}
function calEsc(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function calFirstName(name) {
  return String(name).split(" ")[0];
}
function calFmtRange(from, to) {
  if (calSameDay(from, to)) {
    return from.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }
  const opts = { month: "short", day: "numeric" };
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.toLocaleDateString(undefined, opts)} – ${to.getDate()}`;
  }
  return `${from.toLocaleDateString(undefined, opts)} – ${to.toLocaleDateString(undefined, opts)}`;
}

function calFmtUnits(value) {
  const units = Number(value || 0);
  const formatted = Number.isInteger(units) ? String(units) : units.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  return `${formatted} ${units === 1 ? "day" : "days"}`;
}

function calAccountLabel(accounts) {
  const labels = { flexible: "flexible PTO", holiday_bank: "holiday bank", none: "no balance charge" };
  return (accounts || []).map((account) => labels[account] || account).join(" + ");
}

// Greedily pack overlapping segments into stacked lanes (mutates + returns segs).
// Holidays sort first so they tend to land on the top lane.
function calAssignLanes(segs) {
  segs.sort((a, b) =>
    (a.type === "holiday" ? 0 : 1) - (b.type === "holiday" ? 0 : 1) ||
    a.colStart - b.colStart ||
    b.span - a.span);
  const lanes = [];
  segs.forEach((seg) => {
    const segEnd = seg.colStart + seg.span - 1;
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const fits = lanes[i].every((s) => segEnd < s.colStart || seg.colStart > s.colStart + s.span - 1);
      if (fits) {
        lanes[i].push(seg);
        seg.lane = i;
        placed = true;
        break;
      }
    }
    if (!placed) {
      seg.lane = lanes.length;
      lanes.push([seg]);
    }
  });
  return segs;
}

function calPopoverEl(root) {
  // The popover is portaled to <body> (so no card/overflow/stacking context can
  // clip it); we keep the per-calendar reference on the root element.
  if (!root.__calPop) {
    const pop = document.createElement("div");
    pop.setAttribute("data-pp-cal-pop", "");
    pop.className = "pp-cal-pop hidden";
    document.body.appendChild(pop);
    root.__calPop = pop;
  }
  return root.__calPop;
}

function calClosePopover(root) {
  if (root.__calPop) root.__calPop.classList.add("hidden");
}

function calEventPopHtml(ev) {
  const tone = CAL_TONES[ev.tone] || CAL_TONES.cayenne;
  const dot = CAL_DOTS[ev.tone] || CAL_DOTS.cayenne;
  const approved = ev.status === "approved";
  const statusClass = approved ? "pp-badge-success" : "pp-badge-warning";
  const statusLabel = approved ? "Approved" : "Requested";
  const allocationLabel = calAccountLabel(ev.charge_accounts);
  const detailDays = (ev.days || []).filter((day) => Number(day.units_days) < 1 || (ev.charge_accounts || []).length > 1);
  const detailHtml = detailDays.length
    ? `<div class="mt-1 text-xs text-smoke">${detailDays.map((day) => {
      const account = calAccountLabel([day.charge_account]);
      return `${calEsc(calFmtRange(day.on, day.on))} · ${calEsc(calFmtUnits(day.units_days))}${account ? ` · ${calEsc(account)}` : ""}`;
    }).join("<br>")}</div>`
    : "";
  return (
    `<div class="flex items-center gap-2.5">` +
      `<span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${tone}">${calEsc(ev.initials)}</span>` +
      `<div class="min-w-0 flex-1">` +
        `<div class="truncate text-sm font-semibold text-charcoal">${calEsc(ev.name)}</div>` +
        `<div class="text-xs text-smoke">${calEsc(ev.kind)}</div>` +
      `</div>` +
      `<span class="pp-badge shrink-0 ${statusClass}">${statusLabel}</span>` +
    `</div>` +
    `<div class="mt-2.5 flex items-center gap-1.5 text-xs text-smoke">` +
      `<span class="h-1.5 w-1.5 rounded-full ${dot}"></span>${calEsc(calFmtRange(ev.from, ev.to))}` +
    `</div>` +
    `<div class="mt-2 border-t border-salt/70 pt-2 text-xs text-charcoal">` +
      `<span class="font-semibold">${calEsc(calFmtUnits(ev.units_days))}</span> allocated${allocationLabel ? ` · ${calEsc(allocationLabel)}` : ""}` +
      detailHtml +
    `</div>`
  );
}

function calHolidayPopHtml(h) {
  const people = h.people || [];
  const notTaking = h.not_taking || [];
  const location = h.region ? `${h.country} · ${h.region.toUpperCase()}` : h.country;
  const peopleHtml = people.length
    ? people.map((person) =>
      `<span class="inline-flex items-center gap-1 rounded-full bg-salt/60 py-1 pl-1 pr-2 text-xs font-medium text-charcoal" title="${calEsc(person.name)}">` +
        `<span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ghost text-[9px] font-bold text-cayenne">${calEsc(person.initials)}</span>` +
        `${calEsc(calFirstName(person.name))}` +
      `</span>`
    ).join("")
    : `<span class="text-xs text-smoke">No one is taking this holiday.</span>`;
  const notTakingHtml = notTaking.length
    ? `<div class="mt-1.5 space-y-1">${notTaking.map((person) =>
          `<div class="flex items-center justify-between gap-2 text-xs">` +
            `<span class="truncate font-medium text-charcoal">${calEsc(person.name)}</span>` +
            `<span class="shrink-0 text-smoke">${person.status === "confirmed" ? "credit confirmed" : person.status === "different_region" ? "different region" : "plans to work"}</span>` +
          `</div>`
        ).join("")}</div>`
    : `<div class="mt-1.5 text-xs text-smoke">Everyone on this country team is taking it off.</div>`;
  return (
    `<div class="flex items-center gap-2.5">` +
      `<span class="block h-7 w-10 shrink-0 overflow-hidden rounded border border-salt shadow-sm">${calFlagSvg(h.cc, "xMidYMid meet")}</span>` +
      `<div class="min-w-0">` +
        `<div class="text-sm font-semibold leading-snug text-charcoal">${calEsc(h.name)}</div>` +
        `<div class="text-xs text-smoke">Public holiday · ${calEsc(location)}</div>` +
      `</div>` +
    `</div>` +
    `<div class="mt-2.5 flex items-center gap-1.5 text-xs text-smoke">` +
      `<span class="h-1.5 w-1.5 rounded-full bg-saffron"></span>${calEsc(calFmtRange(h.on, h.on))}` +
    `</div>` +
    `<div class="mt-3 border-t border-salt/70 pt-2.5">` +
      `<div class="flex items-center justify-between gap-2">` +
        `<div class="text-[10px] font-semibold uppercase tracking-wide text-smoke">Taking off</div>` +
        `<span class="text-xs font-semibold text-charcoal">${people.length}</span>` +
      `</div>` +
      `<div class="mt-1.5 flex flex-wrap gap-1.5">${peopleHtml}</div>` +
    `</div>` +
    `<div class="mt-3 border-t border-salt/70 pt-2.5">` +
      `<div class="flex items-center justify-between gap-2">` +
        `<div class="text-[10px] font-semibold uppercase tracking-wide text-smoke">Not taking off</div>` +
        `<span class="text-xs font-semibold text-charcoal">${notTaking.length}</span>` +
      `</div>` +
      notTakingHtml +
    `</div>`
  );
}

function calShowPopover(root, anchorEl, html) {
  const pop = calPopoverEl(root);
  pop.innerHTML = html;
  pop.classList.remove("hidden");

  // Fixed positioning in viewport coordinates: anchor below the bar, clamp
  // horizontally, and flip above the anchor when there's no room below.
  const a = anchorEl.getBoundingClientRect();
  const popW = pop.offsetWidth || 240;
  const popH = pop.offsetHeight || 110;
  const left = Math.max(8, Math.min(a.left, window.innerWidth - popW - 8));
  let top = a.bottom + 6;
  if (top + popH > window.innerHeight - 8) top = Math.max(8, a.top - popH - 6);
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
}

function renderCalendar(root) {
  const today = root.__calToday;
  const vy = root.__calYear;
  const vm = root.__calMonth; // 0-based month
  const monthStart = new Date(vy, vm, 1);
  const monthEnd = new Date(vy, vm + 1, 0);
  const lead = calMondayIndex(monthStart);
  const gridStart = calAddDays(monthStart, -lead);
  const weeks = Math.ceil((lead + monthEnd.getDate()) / 7);

  // Compact mode (home dashboard) uses shorter rows + a single lane so the
  // month fits beside the releases column.
  const compact = root.hasAttribute("data-pp-calendar-compact");
  const headerH = compact ? 15 : CAL_HEADER_H;
  const barH = compact ? 13 : CAL_BAR_H;
  const barGap = compact ? 2 : CAL_BAR_GAP;
  const minLanes = compact ? 1 : CAL_MIN_LANES;
  const ratio = compact ? 0.42 : 0.75;

  // Time-off + public holidays both render as lane-packed event bars.
  const items = root.__calEvents
    .map((ev) => ({ type: "off", ref: ev, from: ev.from, to: ev.to }))
    .concat(root.__calHolidays.map((h) => ({ type: "holiday", ref: h, from: h.on, to: h.on })));

  // Clip every item to each week and pack into lanes.
  const weekSegs = [];
  let maxLanes = 0;
  for (let w = 0; w < weeks; w++) {
    const weekStart = calAddDays(gridStart, w * 7);
    const weekEnd = calAddDays(weekStart, 6);
    const segs = [];
    items.forEach((it) => {
      const segStart = new Date(Math.max(it.from, weekStart, monthStart));
      const segEnd = new Date(Math.min(it.to, weekEnd, monthEnd));
      if (segStart > segEnd) return;
      segs.push({
        type: it.type,
        ref: it.ref,
        colStart: calDiffDays(weekStart, segStart) + 1,
        span: calDiffDays(segStart, segEnd) + 1,
        startsHere: calSameDay(segStart, it.from),
        endsHere: calSameDay(segEnd, it.to),
      });
    });
    calAssignLanes(segs);
    segs.forEach((s) => { maxLanes = Math.max(maxLanes, s.lane + 1); });
    weekSegs.push(segs);
  }
  const lanes = Math.max(minLanes, maxLanes);
  const laneHeight = headerH + lanes * (barH + barGap) + 6;
  // Size each day box to the flag aspect ratio (4:3) so flag backgrounds fill
  // edge-to-edge without cropping; never shorter than what the bars need. The
  // compact (dashboard) calendar is doubled for a taller, airier grid.
  const weeksEl = root.querySelector("[data-pp-calendar-weeks]");
  const colWidth = (weeksEl.clientWidth || 760) / 7;
  const baseHeight = Math.max(laneHeight, colWidth * ratio);
  const weekHeight = Math.round(compact ? baseHeight * 2 : baseHeight);

  // Index public holidays by day.
  const holByKey = {};
  root.__calHolidays.forEach((h) => {
    const k = `${h.on.getFullYear()}-${h.on.getMonth()}-${h.on.getDate()}`;
    (holByKey[k] = holByKey[k] || []).push(h);
  });

  // Background grid: day boxes with date numbers + holiday flags.
  let cells = "";
  for (let w = 0; w < weeks; w++) {
    for (let i = 0; i < 7; i++) {
      const date = calAddDays(gridStart, w * 7 + i);
      const inMonth = date.getMonth() === vm;
      const weekend = date.getDay() === 0 || date.getDay() === 6;
      const dayHols = inMonth ? (holByKey[`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`] || []) : [];
      let num;
      if (calSameDay(date, today)) {
        num = `<span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-cayenne text-[11px] font-bold text-white">${date.getDate()}</span>`;
      } else {
        const cls = !inMonth ? "text-smoke/30" : weekend ? "text-smoke/40" : "text-charcoal";
        num = `<span class="text-xs font-medium ${cls}">${date.getDate()}</span>`;
      }
      let flagLayer = "";
      if (dayHols.length) {
        // Repeated flag ribbon across the bottom third of the day (one strip
        // per holiday); the named bar above carries the holiday detail.
        const strips = dayHols.map((h) =>
          `<button type="button" data-pp-cal-holiday="${h.id}" class="pp-cal-flag-strip" ` +
          `style="background-image:${calFlagDataUri(h.cc)}" title="${calEsc(h.name)} — ${calEsc(h.country)}"></button>`
        ).join("");
        flagLayer = `<div class="pp-cal-flag-row">${strips}</div>`;
      }
      const bgOut = inMonth ? "" : "bg-salt/15";
      cells +=
        `<div class="relative overflow-hidden border-t border-l border-salt/60 ${bgOut}">` +
          flagLayer +
          `<div class="pointer-events-none relative z-10 flex justify-end p-1">${num}</div>` +
        `</div>`;
    }
  }

  // Overlay: spanning event bars (time off + named holiday events).
  let bars = "";
  for (let w = 0; w < weeks; w++) {
    weekSegs[w].forEach((s) => {
      const pctLeft = (((s.colStart - 1) / 7) * 100).toFixed(4);
      const pctWidth = ((s.span / 7) * 100).toFixed(4);
      const top = w * weekHeight + headerH + s.lane * (barH + barGap);
      const roundL = s.startsHere ? "rounded-l-md" : "";
      const roundR = s.endsHere ? "rounded-r-md" : "";
      const pos = `style="left:calc(${pctLeft}% + 3px);width:calc(${pctWidth}% - 6px);top:${top}px;height:${barH}px;"`;
      if (s.type === "holiday") {
        const h = s.ref;
        const people = h.people || [];
        const notTaking = h.not_taking || [];
        const takingNames = people.map((person) => person.name).join(", ");
        const notTakingNames = notTaking.map((person) => person.name).join(", ");
        const location = h.region ? `${h.country} · ${h.region.toUpperCase()}` : h.country;
        const peoplePreview = compact
          ? `${people.length} off`
          : people.length
            ? `${calFirstName(people[0].name)}${people.length > 1 ? ` +${people.length - 1}` : ""}`
            : "No one off";
        const holidayTitle = `${h.name} — ${location}${takingNames ? ` — Taking off: ${takingNames}` : " — No one taking off"}${notTakingNames ? ` — Not taking off: ${notTakingNames}` : ""}`;
        bars +=
          `<button type="button" data-pp-cal-holiday="${h.id}" class="pp-cal-bar pp-cal-bar-holiday ${roundL} ${roundR}" ` +
          `title="${calEsc(holidayTitle)}" ${pos}>` +
          `<span class="mr-1 h-1.5 w-1.5 shrink-0 rounded-full bg-saffron"></span>` +
          `<span class="truncate">${calEsc(h.name)}</span>` +
          `<span class="ml-auto shrink-0 pl-1 text-[10px] text-smoke">${calEsc(peoplePreview)}</span></button>`;
      } else {
        const ev = s.ref;
        const mine = compact && root.__calCurrentUserId != null && ev.user_id === root.__calCurrentUserId;
        const tone = mine ? "pp-cal-bar-mine" : (CAL_TONES[ev.tone] || CAL_TONES.cayenne);
        const label = mine ? "You&rsquo;re off" : calEsc(calFirstName(ev.name));
        const requestedClass = ev.status === "requested" ? "pp-cal-bar-requested" : "";
        bars +=
          `<button type="button" data-pp-cal-event="${ev.id}" class="pp-cal-bar ${tone} ${requestedClass} ${roundL} ${roundR}" ` +
          `title="${calEsc(ev.name)} — ${calEsc(ev.kind)} — ${calEsc(ev.status)} — ${calEsc(calFmtUnits(ev.units_days))}" ${pos}>` +
          `<span class="truncate">${label}</span></button>`;
      }
    });
  }

  weeksEl.style.height = `${weeks * weekHeight}px`;
  weeksEl.innerHTML =
    `<div class="grid grid-cols-7 overflow-hidden rounded-lg border-b border-r border-salt/60" style="grid-auto-rows:${weekHeight}px">${cells}</div>` +
    `<div class="pointer-events-none absolute inset-0">${bars}</div>`;

  const labelEl = root.querySelector("[data-pp-calendar-label]");
  if (labelEl) labelEl.textContent = monthStart.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  calClosePopover(root);
}

function calShiftMonth(root, delta) {
  const d = new Date(root.__calYear, root.__calMonth + delta, 1);
  root.__calYear = d.getFullYear();
  root.__calMonth = d.getMonth();
  renderCalendar(root);
}

function initCalendars() {
  document.querySelectorAll("[data-pp-calendar]").forEach((root) => {
    if (root.__calBound) return;
    root.__calBound = true;
    root.__calToday = calParseISO(root.dataset.ppCalendarToday);
    root.__calYear = root.__calToday.getFullYear();
    root.__calMonth = root.__calToday.getMonth();
    root.__calCurrentUserId = root.dataset.ppCalendarCurrentUserId
      ? Number(root.dataset.ppCalendarCurrentUserId)
      : null;

    const parseJson = (sel) => {
      const el = root.querySelector(sel);
      if (!el) return [];
      try { return JSON.parse(el.textContent); } catch { return []; }
    };
    root.__calEvents = parseJson("[data-pp-calendar-events]").map((e) => ({
      ...e,
      from: calParseISO(e.from),
      to: calParseISO(e.to),
      days: (e.days || []).map((day) => ({ ...day, on: calParseISO(day.on) })),
    }));
    root.__calHolidays = parseJson("[data-pp-calendar-holidays]").map((h) => ({ ...h, on: calParseISO(h.on) }));

    root.addEventListener("click", (event) => {
      if (event.target.closest("[data-pp-calendar-prev]")) { calShiftMonth(root, -1); return; }
      if (event.target.closest("[data-pp-calendar-next]")) { calShiftMonth(root, 1); return; }
      if (event.target.closest("[data-pp-calendar-today-btn]")) {
        root.__calYear = root.__calToday.getFullYear();
        root.__calMonth = root.__calToday.getMonth();
        renderCalendar(root);
        return;
      }
      const bar = event.target.closest("[data-pp-cal-event]");
      if (bar) {
        event.stopPropagation();
        const ev = root.__calEvents.find((x) => String(x.id) === bar.getAttribute("data-pp-cal-event"));
        if (ev) calShowPopover(root, bar, calEventPopHtml(ev));
        return;
      }
      const flag = event.target.closest("[data-pp-cal-holiday]");
      if (flag) {
        event.stopPropagation();
        const h = root.__calHolidays.find((x) => String(x.id) === flag.getAttribute("data-pp-cal-holiday"));
        if (h) calShowPopover(root, flag, calHolidayPopHtml(h));
        return;
      }
      if (!event.target.closest("[data-pp-cal-pop]")) calClosePopover(root);
    });

    renderCalendar(root);
  });
}

function closeAllCalendarPopovers() {
  document.querySelectorAll("[data-pp-calendar]").forEach(calClosePopover);
}

function bindPepperUI() {
  if (window.__pepperUIBound) return;
  window.__pepperUIBound = true;

  document.addEventListener("click", (event) => {
    const themeOption = event.target.closest("[data-pp-set-theme]");
    if (themeOption) {
      event.preventDefault();
      applyThemePreference(themeOption.dataset.ppSetTheme || "system");
      closeAllDropdowns();
      return;
    }

    const themeToggle = event.target.closest("[data-pp-theme-toggle]");
    if (themeToggle) {
      event.preventDefault();
      applyThemePreference(resolvedTheme() === "dark" ? "light" : "dark");
      return;
    }

    const toastClose = event.target.closest("[data-pp-toast-close]");
    if (toastClose) {
      const toast = toastClose.closest("[data-pp-toast]");
      dismissToast(toast);
      return;
    }

    const modalOpen = event.target.closest("[data-pp-modal-open]");
    if (modalOpen) {
      event.preventDefault();
      openModal(modalOpen.getAttribute("data-pp-modal-open"), modalOpen);
      return;
    }
    if (event.target.closest("[data-pp-modal-close]")) {
      event.preventDefault();
      closeAllModals();
      return;
    }

    const mobileButton = event.target.closest("[data-pp-mobile-button]");
    if (mobileButton) {
      toggleMobileNav(mobileButton);
      return;
    }

    const sidebarToggle = event.target.closest("[data-pp-sidebar-toggle]");
    if (sidebarToggle) {
      event.preventDefault();
      setSidebar(!sidebarIsOpen());
      return;
    }

    if (event.target.closest("[data-pp-sidebar-backdrop]")) {
      setSidebar(false);
      return;
    }

    // Close the off-canvas sidebar after following a nav link on small screens.
    if (event.target.closest("[data-pp-sidebar] a") && window.matchMedia("(max-width: 1535px)").matches) {
      setSidebar(false);
    }

    const dropdownButton = event.target.closest("[data-pp-dropdown-button]");
    if (dropdownButton) {
      const dropdown = dropdownButton.closest("[data-pp-dropdown]");
      if (dropdown) toggleDropdown(dropdown);
      return;
    }

    if (event.target.closest("[data-pp-dropdown-menu]")) return;
    closeAllDropdowns();

    // Clicks outside any calendar (and outside its portaled popover) dismiss it.
    if (!event.target.closest("[data-pp-calendar]") && !event.target.closest("[data-pp-cal-pop]")) {
      closeAllCalendarPopovers();
    }
  });

  document.addEventListener("keydown", (event) => {
    const openModalElement = document.querySelector("[data-pp-modal]:not(.hidden)");
    if (openModalElement && event.key === "Tab") {
      trapFocus(openModalElement, event);
      return;
    }
    if (event.key !== "Escape") return;
    closeAllDropdowns();
    closeAllCalendarPopovers();
    if (closeAllModals()) return;
    setSidebar(false);
  });

  // Re-render calendars on resize so day boxes keep the flag (4:3) ratio.
  let calResizePending = false;
  window.addEventListener("resize", () => {
    setSidebar(sidebarIsOpen());
    refreshScrollableTables();
    if (calResizePending) return;
    calResizePending = true;
    requestAnimationFrame(() => {
      calResizePending = false;
      document.querySelectorAll("[data-pp-calendar]").forEach((root) => {
        if (root.__calBound) renderCalendar(root);
      });
    });
  });
}

// --- Modals -----------------------------------------------------------------
let activeModalTrigger = null;

function openModal(id, trigger = document.activeElement) {
  const modal = document.querySelector(`[data-pp-modal="${id}"]`);
  if (!modal) return;
  activeModalTrigger = trigger;
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  document.documentElement.classList.add("overflow-hidden");
  const focusable = modal.querySelector("input, select, textarea, button:not([data-pp-modal-close])");
  if (focusable) focusable.focus();
}

function closeAllModals() {
  let closed = false;
  document.querySelectorAll("[data-pp-modal]").forEach((m) => {
    if (!m.classList.contains("hidden")) {
      m.classList.add("hidden");
      m.setAttribute("aria-hidden", "true");
      closed = true;
    }
  });
  if (closed) {
    document.documentElement.classList.remove("overflow-hidden");
    activeModalTrigger?.focus();
    activeModalTrigger = null;
  }
  return closed;
}

// --- Inline country flags (reuses the calendar's flag SVGs) ------------------
function initFlags() {
  document.querySelectorAll("[data-pp-flag]").forEach((el) => {
    if (el.dataset.ppFlagDone) return;
    const cc = el.dataset.ppFlag;
    if (FLAG_INNER[cc]) {
      el.innerHTML = calFlagSvg(cc, el.dataset.ppFlagFit || "xMidYMid slice");
      el.dataset.ppFlagDone = "1";
    }
  });
}

// --- Leave planning: preferences never create confirmed balance --------------
function initLeave() {
  const root = document.querySelector("[data-pp-leave]");
  if (!root || root.__leaveBound) return;
  root.__leaveBound = true;
  const toggles = Array.from(document.querySelectorAll("[data-pp-ph-toggle]"));
  const set = (key, val) => {
    root.querySelectorAll(`[data-leave="${key}"]`).forEach((el) => {
      el.textContent = String(val);
    });
  };

  const recompute = () => {
    const total = toggles.length;
    const taken = toggles.filter((t) => t.checked).length;
    const plannedWork = toggles.filter((t) => {
      const confirmedWork = t.closest("[data-pp-ph-row]")?.dataset.ppPhConfirmedWork === "true";
      return !t.checked && !confirmedWork;
    }).length;
    set("ph-taken", taken);
    set("ph-total", total);
    set("ph-planned-work", plannedWork);
    const hbar = root.querySelector('[data-leave="ph-bar"]');
    if (hbar) hbar.style.width = `${total ? Math.round((taken / total) * 100) : 0}%`;
    toggles.forEach((t) => {
      const row = t.closest("[data-pp-ph-row]");
      const status = row?.querySelector("[data-pp-ph-status]");
      if (status) {
        const confirmedWork = !t.checked && row.dataset.ppPhConfirmedWork === "true";
        status.textContent = t.checked ? "Taking off" : confirmedWork ? "Credit confirmed" : "Plan to work";
        status.classList.toggle("pp-badge-success", t.checked || confirmedWork);
        status.classList.toggle("pp-badge-warning", !t.checked && !confirmedWork);
      }
    });
  };

  const savePreference = async (form) => {
    if (!form || !window.fetch) return;
    const toggle = form.querySelector("[data-pp-ph-toggle]");
    const persisted = toggle?.dataset.ppPersisted === "1";

    if (form.__ppPreferenceController) form.__ppPreferenceController.abort();
    const controller = new AbortController();
    form.__ppPreferenceController = controller;
    form.setAttribute("aria-busy", "true");
    form.classList.add("opacity-70");

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      const methodInput = form.querySelector('input[name="_method"]');
      const method = (methodInput?.value || form.method || "post").toUpperCase();
      const headers = { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" };
      if (token) headers["X-CSRF-Token"] = token;

      const response = await fetch(form.action, {
        method,
        body: new FormData(form),
        headers,
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Preference save failed: ${response.status}`);

      const data = await response.json();
      if (toggle) {
        if (typeof data.taking_off === "boolean") toggle.checked = data.taking_off;
        toggle.dataset.ppPersisted = toggle.checked ? "1" : "0";
        const row = toggle.closest("[data-pp-ph-row]");
        if (row) row.dataset.ppPhConfirmedWork = "false";
        recompute();
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      if (toggle) {
        toggle.checked = persisted;
        recompute();
      }
      console.warn("Could not update public holiday preference", error);
    } finally {
      if (form.__ppPreferenceController === controller) {
        delete form.__ppPreferenceController;
        form.removeAttribute("aria-busy");
        form.classList.remove("opacity-70");
      }
    }
  };

  document.querySelectorAll("[data-pp-ph-form]").forEach((form) => {
    if (form.__ppPreferenceBound) return;
    form.__ppPreferenceBound = true;
    const toggle = form.querySelector("[data-pp-ph-toggle]");
    if (toggle) toggle.dataset.ppPersisted = toggle.checked ? "1" : "0";
    form.addEventListener("submit", (event) => {
      if (!window.fetch) return;
      event.preventDefault();
      savePreference(form);
    });
  });

  toggles.forEach((t) => t.addEventListener("change", recompute));
  recompute();
}

// --- Live local clocks (team roster / profile) ------------------------------
function initClocks() {
  const els = document.querySelectorAll("[data-pp-clock]");
  if (!els.length) return;
  const render = () => {
    const now = new Date();
    els.forEach((el) => {
      const tz = el.getAttribute("data-pp-clock");
      try {
        el.textContent = new Intl.DateTimeFormat([], { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(now);
      } catch (_) {
        /* unknown tz — leave the placeholder */
      }
    });
  };
  render();
  if (window.__ppClockTimer) clearInterval(window.__ppClockTimer);
  window.__ppClockTimer = setInterval(render, 30000);
}

// --- Team local time: live clock-period timeline by IANA timezone ------------
const SUN_TONE = {
  cayenne: "#c62828", ember: "#e53935", habanero: "#e65100", sriracha: "#ff6d00",
  saffron: "#f9a825", jalapeno: "#2e7d32", cinnamon: "#795548", peppercorn: "#1c1917",
};
// The band communicates clock periods, not astronomical sunrise or sunset.
const SUN_SKY = [[0,[35,42,58]],[5,[47,55,73]],[6,[223,150,96]],[7,[232,212,184]],[8.5,[234,239,244]],[12,[237,241,246]],[15.5,[234,239,244]],[17,[232,212,184]],[18,[223,150,96]],[19,[47,55,73]],[24,[35,42,58]]];
const sunRGB = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
const SUN_GRAD = "linear-gradient(90deg," + SUN_SKY.map(([h, c]) => `${sunRGB(c)} ${((h / 24) * 100).toFixed(1)}%`).join(",") + ")";
const sunSunIco = (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" style="flex:none"><circle cx="12" cy="12" r="3.8" stroke="currentColor" stroke-width="1.8"/><g stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M12 3.2v2M12 18.8v2M3.2 12h2M18.8 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4"/></g></svg>`;
const sunMoonIco = (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" style="flex:none"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" stroke="currentColor" stroke-width="1.7"/></svg>`;
const sunEsc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function sunAvatar(member, size = "normal") {
  const color = SUN_TONE[member.tone] || SUN_TONE.cayenne;
  return `<span class="pp-sun-avatar pp-sun-avatar-${size}" style="--pp-sun-avatar-color:${color}" aria-hidden="true">${sunEsc(member.init)}</span>`;
}

function sunMemberLink(member, layout = "list") {
  const id = encodeURIComponent(String(member.id));
  return `<a class="pp-sun-member pp-sun-member-${layout}" href="/team/${id}" data-pp-sun-member="${id}" title="${sunEsc(member.name)}">
    ${sunAvatar(member, layout === "card" ? "small" : "normal")}
    <span class="pp-sun-member-name">${sunEsc(member.name)}</span>
  </a>`;
}

function sunMarker(group, compact = false) {
  const position = Math.max(compact ? 3 : 0.5, Math.min(compact ? 97 : 99.5, (group.hourFraction / 24) * 100));
  return `<span class="pp-sun-marker ${group.daytime ? "is-daytime" : "is-later"}" style="left:${position.toFixed(2)}%"></span>`;
}

function sunBand(groups, compact = false) {
  return `<div class="pp-sun-band" style="--pp-sun-gradient:${SUN_GRAD}" aria-hidden="true">${groups.map((group) => sunMarker(group, compact)).join("")}</div>`;
}

function sunInvalidMarkup(members) {
  if (!members.length) return "";

  const links = members.map((member) => sunMemberLink(member)).join("");
  return `<aside class="pp-sun-warning" aria-label="Timezone settings need attention">
    <div>
      <p class="pp-sun-warning-title">Timezone unavailable for ${members.length === 1 ? "1 teammate" : `${members.length} teammates`}</p>
      <p class="pp-sun-warning-copy">Update ${members.length === 1 ? "their profile" : "their profiles"} to place them on the timeline.</p>
    </div>
    <div class="pp-sun-warning-members">${links}</div>
  </aside>`;
}

function sunListMarkup(groups) {
  const cards = groups.map((group) => {
    const members = group.members.map((member) => `<li>${sunMemberLink(member)}</li>`).join("");
    const icon = group.daytime ? sunSunIco(16) : sunMoonIco(16);
    return `<article class="pp-sun-list-card ${group.daytime ? "is-daytime" : "is-later"}" role="listitem" aria-label="${sunEsc(group.time)}, ${sunEsc(group.label)}">
      <div class="pp-sun-list-heading">
        <span class="pp-sun-clock-icon" aria-hidden="true">${icon}</span>
        <div class="min-w-0">
          <p class="pp-sun-time">${sunEsc(group.time)}</p>
          <p class="pp-sun-zone" title="${sunEsc(group.label)}">${sunEsc(group.label)} · ${sunEsc(localTimePeriod(group.hour))}</p>
        </div>
      </div>
      <ul class="pp-sun-members" aria-label="${group.members.length === 1 ? "1 teammate" : `${group.members.length} teammates`}">${members}</ul>
    </article>`;
  }).join("");

  return `${sunBand(groups, true)}<div class="pp-sun-list" role="list">${cards}</div>`;
}

function sunTimelineMarkup(groups, width) {
  const gap = 12;
  const cardWidth = Math.min(210, Math.floor((width - (groups.length - 1) * gap) / groups.length));
  const centerX = (index) => groups.length === 1 ? width / 2 : cardWidth / 2 + index * ((width - cardWidth) / (groups.length - 1));
  const cardHeights = groups.map((group) => {
    const rows = Math.ceil(group.members.length / 2);
    return 82 + rows * 44 + Math.max(0, rows - 1) * 6;
  });
  const bandY = Math.max(...cardHeights) + 46;
  const height = bandY + 12;
  let connectors = `<svg class="pp-sun-connectors" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" aria-hidden="true">`;

  groups.forEach((group, index) => {
    const markerX = Math.max(5, Math.min(width - 5, (group.hourFraction / 24) * width));
    const startX = centerX(index);
    const startY = cardHeights[index];
    const curve = (bandY - startY) * 0.55;
    connectors += `<path d="M ${startX.toFixed(1)} ${startY} C ${startX.toFixed(1)} ${(startY + curve).toFixed(1)} ${markerX.toFixed(1)} ${(bandY - curve).toFixed(1)} ${markerX.toFixed(1)} ${bandY}"/>`;
  });
  connectors += "</svg>";

  const cards = groups.map((group, index) => {
    const members = group.members.map((member) => `<li>${sunMemberLink(member, "card")}</li>`).join("");
    const icon = group.daytime ? sunSunIco(16) : sunMoonIco(16);
    return `<article class="pp-sun-card ${group.daytime ? "is-daytime" : "is-later"}" style="left:${(centerX(index) - cardWidth / 2).toFixed(1)}px;width:${cardWidth}px;height:${cardHeights[index]}px" role="listitem" aria-label="${sunEsc(group.time)}, ${sunEsc(group.label)}">
      <div class="pp-sun-card-heading">
        <span class="pp-sun-clock-icon" aria-hidden="true">${icon}</span>
        <p class="pp-sun-time">${sunEsc(group.time)}</p>
      </div>
      <p class="pp-sun-zone" title="${sunEsc(group.label)}">${sunEsc(group.label)} · ${group.members.length}</p>
      <ul class="pp-sun-card-members" aria-label="${group.members.length === 1 ? "1 teammate" : `${group.members.length} teammates`}">${members}</ul>
    </article>`;
  }).join("");

  const markers = groups.map((group) => {
    const markerX = Math.max(5, Math.min(width - 5, (group.hourFraction / 24) * width));
    return `<span class="pp-sun-marker ${group.daytime ? "is-daytime" : "is-later"}" style="left:${markerX.toFixed(1)}px;top:${bandY}px"></span>`;
  }).join("");
  const band = `<div class="pp-sun-band pp-sun-band-timeline" style="top:${bandY - 6}px;--pp-sun-gradient:${SUN_GRAD}" aria-hidden="true"></div>`;

  return { height, html: connectors + band + markers + cards };
}

function sunBuild(root) {
  const mount = root.querySelector("[data-pp-sun-strip]");
  const dataEl = root.querySelector("[data-pp-sun-data]");
  if (!mount || !dataEl) return;
  let team;
  try {
    team = JSON.parse(dataEl.textContent);
  } catch (_) {
    mount.dataset.layout = "error";
    mount.innerHTML = `<div class="pp-sun-empty" role="alert"><p class="pp-sun-empty-title">Local times couldn't be loaded</p><p>Refresh the page to try again.</p></div>`;
    return;
  }

  const W = mount.clientWidth || 800;
  const now = new Date();
  const { groups, invalidMembers } = groupTeamByLocalTime(team, now);
  const daytimeCount = groups.reduce((total, group) => total + (group.daytime ? group.members.length : 0), 0);
  const outsideCount = groups.reduce((total, group) => total + (group.daytime ? 0 : group.members.length), 0);

  const legend = root.querySelector("[data-pp-sun-legend]");
  if (legend) {
    const legendMarkup = team.length ?
      `<span class="pp-sun-legend-item"><span aria-hidden="true">${sunSunIco()}</span><b>${daytimeCount}</b> in daytime hours</span>` +
      `<span class="pp-sun-legend-item"><span aria-hidden="true">${sunMoonIco()}</span><b>${outsideCount}</b> outside daytime hours</span>` +
      (invalidMembers.length ? `<span class="pp-sun-legend-warning"><b>${invalidMembers.length}</b> unavailable</span>` : "") :
      "No team members";
    if (legend.innerHTML !== legendMarkup) legend.innerHTML = legendMarkup;
  }

  if (!team.length) {
    mount.dataset.layout = "empty";
    mount.style.height = "auto";
    mount.innerHTML = `<div class="pp-sun-empty"><p class="pp-sun-empty-title">No teammates to show yet</p><p>Active team members will appear here after they sync from Slack.</p></div>`;
    return;
  }

  const focusedMember = mount.contains(document.activeElement) ? document.activeElement.closest("[data-pp-sun-member]")?.dataset.ppSunMember : null;
  const forceCompact = root.hasAttribute("data-pp-sun-compact");
  const listLayout = shouldUseListLayout(W, groups, forceCompact);
  const invalidMarkup = sunInvalidMarkup(invalidMembers);

  if (!groups.length) {
    mount.dataset.layout = "unavailable";
    mount.style.height = "auto";
    mount.innerHTML = `<div class="pp-sun-empty"><p class="pp-sun-empty-title">No local times to show</p><p>Add valid IANA timezones to team profiles.</p></div>${invalidMarkup}`;
  } else if (listLayout) {
    mount.dataset.layout = "list";
    mount.style.height = "auto";
    mount.innerHTML = sunListMarkup(groups) + invalidMarkup;
  } else {
    const timeline = sunTimelineMarkup(groups, W);
    mount.dataset.layout = "timeline";
    mount.style.height = "auto";
    mount.innerHTML = `<div class="pp-sun-timeline" style="height:${timeline.height}px" role="list">${timeline.html}</div>${invalidMarkup}`;
  }

  if (focusedMember) mount.querySelector(`[data-pp-sun-member="${CSS.escape(focusedMember)}"]`)?.focus();
}

function initSun() {
  const roots = document.querySelectorAll("[data-pp-sun]");
  if (!roots.length) return;
  const renderAll = () => roots.forEach(sunBuild);
  renderAll();

  roots.forEach((root) => {
    root.__ppSunObserver?.disconnect();
    if ("ResizeObserver" in window) {
      let raf;
      root.__ppSunObserver = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => sunBuild(root));
      });
      root.__ppSunObserver.observe(root.querySelector("[data-pp-sun-strip]"));
    }
  });

  if (window.__ppSunTimer) clearInterval(window.__ppSunTimer);
  window.__ppSunTimer = setInterval(renderAll, 60000);
}

// --- Standup calendar (member profile): per-day did/missed/leave + entry -----
const SU_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function suKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function suLong(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Intl.DateTimeFormat([], { weekday: "short", month: "short", day: "numeric" }).format(new Date(y, m - 1, d));
}
function suEsc(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}
function suDetail(rec, key) {
  const wrap = (inner) => `<div class="h-full rounded-xl border border-salt bg-ghost/70 p-4">${inner}</div>`;
  if (!rec) return wrap(`<div class="flex h-full min-h-28 items-center justify-center text-center text-sm text-smoke">Select a day to read its standup.</div>`);
  const head = (badge) => `<div class="flex items-center justify-between gap-2"><div class="font-semibold text-charcoal">${suLong(key)}</div>${badge}</div>`;
  const chip = (cls, text) => `<span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}">${text}</span>`;
  if (rec.status === "done") {
    const e = rec.entry || {};
    const row = (label, val) => `<div><dt class="text-[11px] font-semibold uppercase tracking-wide text-smoke">${label}</dt><dd class="mt-0.5 text-charcoal">${suEsc(val)}</dd></div>`;
    return wrap(`${head(chip("bg-jalapeno/15 text-jalapeno", "Did standup"))}<dl class="mt-3 space-y-3 text-sm">${row("Yesterday", e.yesterday)}${row("Today", e.today)}${row("Blockers", e.blockers)}</dl>`);
  }
  if (rec.status === "missed") {
    return wrap(`${head(chip("bg-cayenne/15 text-cayenne", "Missed"))}<p class="mt-3 text-sm text-smoke">No standup was posted this day.</p>`);
  }
  if (rec.status === "leave") {
    return wrap(`${head(chip("bg-saffron/15 text-habanero", "On leave"))}<p class="mt-3 text-sm text-charcoal">${suEsc(rec.kind || "Time off")} — away this day.</p>`);
  }
  if (rec.status === "weekend") {
    return wrap(`${head(chip("bg-salt text-smoke", "Weekend"))}<p class="mt-3 text-sm text-smoke">No standup expected.</p>`);
  }
  return wrap(`${head(chip("bg-salt text-smoke", "Upcoming"))}<p class="mt-3 text-sm text-smoke">No standup yet.</p>`);
}
function initStandup() {
  document.querySelectorAll("[data-pp-standup]").forEach((root) => {
    if (root.__suBound) return;
    const dataEl = root.querySelector("[data-pp-standup-data]");
    const grid = root.querySelector("[data-pp-standup-grid]");
    const detail = root.querySelector("[data-pp-standup-detail]");
    const monthSelect = root.querySelector("[data-pp-standup-month]");
    const endpoint = root.getAttribute("data-pp-standup-url");
    if (!dataEl || !grid) return;
    let recs;
    try { recs = JSON.parse(dataEl.textContent); } catch (_) { return; }
    root.__suBound = true;

    const byDate = {};
    recs.forEach((r) => (byDate[r.on] = r));
    const today = new Date();
    const todayKey = suKey(today);
    let view = new Date(today.getFullYear(), today.getMonth(), 1);
    let selected = byDate[todayKey] ? todayKey : null;
    const monthKey = (date) => suKey(new Date(date.getFullYear(), date.getMonth(), 1)).slice(0, 7);
    const monthDate = (key) => {
      const [year, month] = key.split("-").map(Number);
      return new Date(year, month - 1, 1);
    };
    const monthLabel = (key) => {
      const date = monthDate(key);
      return `${SU_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
    };
    const historyStart = monthSelect?.getAttribute("data-pp-standup-start") || monthKey(today);
    const addMonthOption = (key, before = null) => {
      if (!monthSelect || monthSelect.querySelector(`option[value="${key}"]`)) return;
      const option = new Option(monthLabel(key), key);
      monthSelect.add(option, before);
    };
    if (monthSelect) {
      let optionDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const firstDate = monthDate(historyStart);
      while (optionDate >= firstDate) {
        addMonthOption(monthKey(optionDate));
        optionDate = new Date(optionDate.getFullYear(), optionDate.getMonth() - 1, 1);
      }
    }
    const loadedMonths = new Set(recs.map((record) => record.on.slice(0, 7)));
    const pendingMonths = new Map();
    let failedMonth = null;
    const CLS = { done: "pp-su-done", missed: "pp-su-missed", leave: "pp-su-leave", weekend: "pp-su-weekend", upcoming: "pp-su-upcoming" };

    const render = () => {
      const y = view.getFullYear(), m = view.getMonth();
      const key = monthKey(view);
      if (monthSelect) {
        const firstOption = monthSelect.options[0];
        const before = firstOption && key > firstOption.value ? firstOption : null;
        addMonthOption(key, before);
        monthSelect.value = key;
      }
      if (!loadedMonths.has(key)) {
        const failed = failedMonth === key;
        grid.innerHTML = `<div class="flex min-h-64 items-center justify-center rounded-xl border border-salt bg-ghost/70 p-4 text-center text-sm text-smoke">${failed ? `Couldn’t load this month. <button type="button" class="pp-link ml-1" data-su-retry>Try again</button>` : "Loading standups…"}</div>`;
        if (detail) detail.innerHTML = suDetail(null, null);
        return;
      }
      const w1 = (new Date(y, m, 1).getDay() + 6) % 7;
      const lead = w1 <= 4 ? w1 : 0; // Mon–Fri grid: weekend starts clamp to col 0
      const dim = new Date(y, m + 1, 0).getDate();
      let cells = ["Mo", "Tu", "We", "Th", "Fr"].map((w) => `<div class="pp-su-head">${w}</div>`).join("");
      for (let i = 0; i < lead; i++) cells += `<div class="pp-su-day pp-su-empty"></div>`;
      for (let d = 1; d <= dim; d++) {
        if ((new Date(y, m, d).getDay() + 6) % 7 > 4) continue; // skip Sat/Sun
        const key = suKey(new Date(y, m, d));
        const r = byDate[key];
        const st = r ? r.status : "upcoming";
        const dot = st === "done" || st === "missed" || st === "leave" ? `<span class="pp-su-dot"></span>` : "";
        cells += `<button type="button" data-su-date="${key}" class="pp-su-day ${CLS[st] || ""} ${key === todayKey ? "pp-su-today" : ""} ${key === selected ? "pp-su-sel" : ""}"><span class="pp-su-num">${d}</span>${dot}</button>`;
      }
      grid.innerHTML = `<div class="pp-su-grid">${cells}</div>`;
      if (detail) detail.innerHTML = suDetail(selected ? byDate[selected] : null, selected);
    };

    const loadMonth = (date) => {
      const key = monthKey(date);
      if (loadedMonths.has(key)) return Promise.resolve();
      if (pendingMonths.has(key)) return pendingMonths.get(key);
      if (!endpoint) {
        failedMonth = key;
        render();
        return Promise.resolve();
      }

      failedMonth = null;
      root.setAttribute("aria-busy", "true");
      render();
      const url = new URL(endpoint, window.location.href);
      url.searchParams.set("month", key);
      const request = fetch(url.toString(), { headers: { Accept: "application/json" } })
        .then((response) => {
          if (!response.ok) throw new Error(`Standup history request failed (${response.status})`);
          return response.json();
        })
        .then((payload) => {
          if (payload.month !== key || !Array.isArray(payload.records)) throw new Error("Invalid standup history response");
          payload.records.forEach((record) => (byDate[record.on] = record));
          loadedMonths.add(key);
          if (failedMonth === key) failedMonth = null;
        })
        .catch(() => {
          failedMonth = key;
        })
        .finally(() => {
          pendingMonths.delete(key);
          if (!pendingMonths.size) root.removeAttribute("aria-busy");
          if (monthKey(view) === key) render();
        });
      pendingMonths.set(key, request);
      return request;
    };

    grid.addEventListener("click", (e) => {
      if (e.target.closest("[data-su-retry]")) {
        loadMonth(view);
        return;
      }
      const cell = e.target.closest("[data-su-date]");
      if (!cell) return;
      selected = cell.getAttribute("data-su-date");
      render();
    });
    const go = (delta) => {
      view = new Date(view.getFullYear(), view.getMonth() + delta, 1);
      selected = null;
      render();
      loadMonth(view);
    };
    root.querySelector("[data-pp-standup-prev]")?.addEventListener("click", () => go(-1));
    root.querySelector("[data-pp-standup-next]")?.addEventListener("click", () => go(1));
    monthSelect?.addEventListener("change", () => {
      view = monthDate(monthSelect.value);
      selected = null;
      render();
      loadMonth(view);
    });
    root.querySelector("[data-pp-standup-today]")?.addEventListener("click", () => {
      view = new Date(today.getFullYear(), today.getMonth(), 1);
      selected = byDate[todayKey] ? todayKey : selected;
      render();
    });
    render();
  });
}

// --- Infra quick jump: registry-backed command palette -----------------------
function initInfraQuickJump() {
  const escapeHtml = (value) => String(value || "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;",
  })[char]);

  const isTypingTarget = (target) => {
    if (!target) return false;
    const tag = target.tagName || "";
    return ["INPUT", "TEXTAREA", "SELECT"].includes(tag) || target.isContentEditable;
  };

  document.querySelectorAll("[data-pp-infra-jump]").forEach((root) => {
    if (root.__infraJumpBound) return;
    const opener = root.querySelector("[data-pp-infra-jump-open]");
    const backdrop = root.querySelector("[data-pp-infra-jump-backdrop]");
    const panel = root.querySelector("[data-pp-infra-jump-panel]");
    const input = root.querySelector("[data-pp-infra-jump-input]");
    const list = root.querySelector("[data-pp-infra-jump-list]");
    const closeButton = root.querySelector("[data-pp-infra-jump-close]");
    const status = root.querySelector("[data-pp-infra-jump-status]");
    const dataNode = root.querySelector("[data-pp-infra-jump-items]");
    if (!opener || !backdrop || !panel || !input || !list || !dataNode) return;

    let items = [];
    try {
      items = JSON.parse(dataNode.textContent || "[]");
    } catch {
      items = [];
    }

    const defaultItems = () => items.filter((item) => item.defaultVisible !== false);

    let filtered = defaultItems();
    let selected = 0;
    let open = false;
    let previousFocus = null;
    let inertedElements = [];

    const setOutsideInert = (inert) => {
      if (!inert) {
        inertedElements.forEach((element) => { element.inert = false; });
        inertedElements = [];
        return;
      }

      const main = panel.closest("main");
      const candidates = [document.querySelector("[data-pp-sidebar]"), ...(main ? Array.from(main.children) : [])];
      inertedElements = candidates.filter((element) => element && element !== root && !element.contains(panel) && !element.inert);
      inertedElements.forEach((element) => { element.inert = true; });
    };

    const draw = () => {
      const visible = filtered.slice(0, 20);
      if (!visible.length) {
        list.innerHTML = '<div class="pp-infra-jump-empty">No matches</div>';
        input.removeAttribute("aria-activedescendant");
        if (status) status.textContent = "0 results";
        return;
      }

      list.innerHTML = visible.map((item, index) => {
        const activeClass = index === selected ? " is-active" : "";
        return `<a id="infra-quick-jump-option-${index}" class="pp-infra-jump-item${activeClass}" href="${escapeHtml(item.href)}" role="option" tabindex="-1" aria-selected="${index === selected}" data-pp-infra-jump-index="${index}">
          <span class="min-w-0">
            <span class="pp-infra-jump-label">${escapeHtml(item.label)}</span>
            <span class="pp-infra-jump-detail">${escapeHtml(item.detail)}</span>
          </span>
          <span class="pp-infra-jump-kind">${escapeHtml(item.kind)}</span>
        </a>`;
      }).join("");
      input.setAttribute("aria-activedescendant", `infra-quick-jump-option-${selected}`);
      if (status) status.textContent = `${visible.length} result${visible.length === 1 ? "" : "s"}`;
    };

    const applyFilter = () => {
      const query = input.value.trim().toLowerCase();
      filtered = query ? items.filter((item) => String(item.haystack || "").includes(query)) : defaultItems();
      selected = 0;
      draw();
    };

    const setOpen = (nextOpen) => {
      open = nextOpen;
      backdrop.classList.toggle("hidden", !open);
      panel.classList.toggle("hidden", !open);
      panel.setAttribute("aria-hidden", open ? "false" : "true");
      opener.setAttribute("aria-expanded", open ? "true" : "false");
      input.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        previousFocus = document.activeElement;
        setOutsideInert(true);
        input.value = "";
        applyFilter();
        input.focus();
      } else {
        input.removeAttribute("aria-activedescendant");
        setOutsideInert(false);
        if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
      }
    };

    const go = () => {
      const item = filtered[selected];
      if (item?.href) window.location.href = item.href;
    };

    input.addEventListener("input", applyFilter);
    input.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        selected = Math.min(selected + 1, Math.max(Math.min(filtered.length, 20) - 1, 0));
        draw();
        list.querySelector(".is-active")?.scrollIntoView({ block: "nearest" });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        selected = Math.max(selected - 1, 0);
        draw();
        list.querySelector(".is-active")?.scrollIntoView({ block: "nearest" });
      } else if (event.key === "Enter") {
        event.preventDefault();
        go();
      }
    });
    panel.addEventListener("keydown", (event) => {
      if (event.key === "Tab") {
        trapFocus(panel, event);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    });
    list.addEventListener("mouseover", (event) => {
      const result = event.target.closest("[data-pp-infra-jump-index]");
      if (!result) return;
      const nextSelected = Number(result.getAttribute("data-pp-infra-jump-index")) || 0;
      if (nextSelected === selected) return;
      selected = nextSelected;
      draw();
    });
    opener.addEventListener("click", () => setOpen(true));
    backdrop.addEventListener("click", () => setOpen(false));
    closeButton?.addEventListener("click", () => setOpen(false));

    root.__infraJumpBound = true;
    root.__infraJumpOpen = () => setOpen(true);
    root.__infraJumpClose = () => setOpen(false);
    root.__infraJumpIsOpen = () => open;
  });

  if (document.__infraQuickJumpKeysBound) return;
  document.addEventListener("keydown", (event) => {
    const root = document.querySelector("[data-pp-infra-jump]");
    if (!root?.__infraJumpOpen) return;
    const typing = isTypingTarget(document.activeElement);

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      if (root.__infraJumpIsOpen()) root.__infraJumpClose();
      else root.__infraJumpOpen();
    } else if (event.key === "/" && !typing && !root.__infraJumpIsOpen()) {
      event.preventDefault();
      root.__infraJumpOpen();
    }
  });
  document.__infraQuickJumpKeysBound = true;
}

// --- Infra tables: lightweight live filtering over already-rendered rows -----
function initInfraFilterableTables() {
  document.querySelectorAll("[data-pp-infra-filterable]").forEach((root) => {
    if (root.__infraFilterableBound) return;
    const input = root.querySelector("[data-pp-infra-filter-input]");
    if (!input) return;
    root.__infraFilterableBound = true;

    const rows = () => Array.from(root.querySelectorAll("[data-pp-infra-filter-row]"));
    const empty = root.querySelector("[data-pp-infra-filter-empty]");
    const rowText = (row) => (row.getAttribute("data-pp-infra-filter-row") || row.textContent || "").toLowerCase();

    const apply = () => {
      const query = input.value.trim().toLowerCase();
      let visible = 0;

      rows().forEach((row) => {
        const hidden = !!query && !rowText(row).includes(query);
        row.classList.toggle("pp-infra-map-hidden", hidden);
        if (!hidden) visible += 1;
      });

      if (empty) empty.classList.toggle("hidden", !query || visible > 0);
    };

    input.addEventListener("input", apply);
    apply();
  });
}

// --- Wide tables: discoverable native scrolling on laptop screens -----------
function refreshScrollableTables() {
  document.querySelectorAll("[data-pp-table-frame]").forEach((frame) => frame.__ppTableScrollUpdate?.());
}

function initScrollableTables() {
  document.querySelectorAll("[data-pp-table-frame]").forEach((frame) => {
    if (frame.__ppTableScrollBound) return;
    const scroller = frame.querySelector("[data-pp-table-scroll]");
    const controls = frame.querySelector("[data-pp-table-scroll-controls]");
    const left = frame.querySelector("[data-pp-table-scroll-left]");
    const right = frame.querySelector("[data-pp-table-scroll-right]");
    if (!scroller || !controls || !left || !right) return;

    const update = () => {
      const max = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      const overflows = max > 1;
      controls.classList.toggle("hidden", !overflows);
      controls.setAttribute("aria-hidden", overflows ? "false" : "true");
      left.disabled = !overflows || scroller.scrollLeft <= 1;
      right.disabled = !overflows || scroller.scrollLeft >= max - 1;
    };
    const move = (direction) => scroller.scrollBy({ left: direction * Math.max(scroller.clientWidth * 0.8, 240) });

    left.addEventListener("click", () => move(-1));
    right.addEventListener("click", () => move(1));
    scroller.addEventListener("scroll", update, { passive: true });
    frame.__ppTableScrollBound = true;
    frame.__ppTableScrollUpdate = update;
    update();
  });
}

// --- Infra map: live filtering and collapsible provider tree -----------------
function initInfraMap() {
  document.querySelectorAll("[data-pp-infra-map]").forEach((root) => {
    if (root.__infraMapBound) return;
    const input = root.querySelector("[data-pp-infra-map-search]");
    const tree = root.querySelector("[data-pp-infra-map-tree]");
    const results = root.querySelector("[data-pp-infra-map-results]") || tree;
    const count = root.querySelector("[data-pp-infra-map-count]");
    const empty = root.querySelector("[data-pp-infra-map-empty]");
    if (!input || !results) return;
    root.__infraMapBound = true;

    const searchable = () => Array.from(results.querySelectorAll("[data-pp-infra-map-searchable]"));
    const textFor = (el) => (el.getAttribute("data-pp-infra-map-searchable") || "").toLowerCase();
    const matchingRecordText = (visible) => `${visible} matching ${Number(visible) === 1 ? "record" : "records"}`;
    const descendantsMatch = (el, query) =>
      Array.from(el.querySelectorAll("[data-pp-infra-map-searchable]")).some((child) => textFor(child).includes(query));
    const hideEmptySections = (query) => {
      Array.from(results.querySelectorAll("section")).forEach((section) => {
        const rows = Array.from(section.querySelectorAll("[data-pp-infra-map-searchable]"));
        if (!rows.length) return;

        const hasVisibleRow = rows.some((row) => !row.closest("[data-pp-infra-map-searchable].pp-infra-map-hidden"));
        section.classList.toggle("pp-infra-map-hidden", !!query && !hasVisibleRow);
      });
    };

    const apply = () => {
      const query = input.value.trim().toLowerCase();
      let visibleItems = 0;
      let visibleLeaves = 0;

      searchable().forEach((el) => {
        const direct = !query || textFor(el).includes(query);
        const child = query && descendantsMatch(el, query);
        const hidden = query && !direct && !child;
        el.classList.toggle("pp-infra-map-hidden", hidden);
        el.classList.toggle("pp-infra-map-hit", !!query && direct);
        if (query && !hidden && el.tagName === "DETAILS") el.open = true;
        if (!hidden) visibleItems += 1;
        if (!el.querySelector("[data-pp-infra-map-searchable]") && !hidden) visibleLeaves += 1;
      });
      hideEmptySections(query);
      if (empty) empty.classList.toggle("hidden", !query || visibleItems > 0);

      if (count) {
        const total = results.getAttribute("data-pp-infra-map-total") || tree?.getAttribute("data-pp-infra-map-total") || String(visibleLeaves);
        count.textContent = matchingRecordText(query ? visibleLeaves : total);
      }
    };

    input.addEventListener("input", apply);
    root.querySelectorAll("[data-pp-infra-map-expand]").forEach((button) => {
      button.addEventListener("click", () => {
        results.querySelectorAll("details").forEach((details) => { details.open = true; });
      });
    });
    root.querySelectorAll("[data-pp-infra-map-collapse]").forEach((button) => {
      button.addEventListener("click", () => {
        results.querySelectorAll("details").forEach((details) => { details.open = false; });
      });
    });

    apply();
  });
}

// --- Infra map: SVG network graph (providers -> hosts/VMs -> services) -------
function resetInfraGraphRoot(root) {
  const searchBinding = root.__infraGraphSearchBinding;
  if (searchBinding) {
    searchBinding.input.removeEventListener("input", searchBinding.handler);
    root.__infraGraphSearchBinding = null;
  }

  root.querySelectorAll("[data-pp-infra-generated-graph]").forEach((el) => el.remove());
  root.__infraGraphBound = false;
}

function resetInfraGraphs() {
  document.querySelectorAll("[data-pp-infra-graph]").forEach(resetInfraGraphRoot);
}

function initInfraGraph() {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const HOST_W = 216;
  const VM_INDENT = 26;
  const HOST_H = 40;
  const VM_H = 36;
  const ROW_GAP = 10;
  const PROVIDER_HEADER_H = 30;
  const PROVIDER_PAD = 12;
  const PROVIDER_GAP = 20;
  const EDGE_SPAN = 240;
  const SVC_W = 240;
  const SVC_GAP = 16;
  const PAD = 8;

  const svgEl = (name, attrs = {}, parent = null) => {
    const el = document.createElementNS(SVG_NS, name);
    Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, String(value)));
    if (parent) parent.appendChild(el);
    return el;
  };
  const truncate = (text, max) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);
  const money = (value) => `$${Number(value || 0).toFixed(2)}/mo`;

  document.querySelectorAll("[data-pp-infra-graph]").forEach((root) => {
    resetInfraGraphRoot(root);
    const dataNode = root.querySelector("[data-pp-infra-graph-data]");
    if (!dataNode) return;
    root.__infraGraphBound = true;

    let graph;
    try {
      graph = JSON.parse(dataNode.textContent || "{}");
    } catch {
      return;
    }
    const providers = Array.isArray(graph.providers) ? graph.providers : [];
    const services = Array.isArray(graph.services) ? graph.services : [];
    if (!providers.length) {
      const emptyNote = document.createElement("p");
      emptyNote.className = "text-sm text-smoke";
      emptyNote.dataset.ppInfraGeneratedGraph = "true";
      emptyNote.textContent = "No assets match the current map filters.";
      root.appendChild(emptyNote);
      return;
    }

    // --- layout ---------------------------------------------------------
    const hostNodes = [];
    const providerBoxes = [];
    let y = PAD;
    providers.forEach((provider) => {
      const top = y;
      y += PROVIDER_HEADER_H;
      (provider.hosts || []).forEach((host) => {
        hostNodes.push({ ...host, provider: provider.provider, x: PAD + 12, y, w: HOST_W, h: HOST_H, vm: false });
        y += HOST_H + ROW_GAP;
        (host.children || []).forEach((child) => {
          hostNodes.push({ ...child, provider: provider.provider, parent: host.id, x: PAD + 12 + VM_INDENT, y, w: HOST_W - VM_INDENT, h: VM_H, vm: true });
          y += VM_H + ROW_GAP;
        });
      });
      y += PROVIDER_PAD - ROW_GAP;
      providerBoxes.push({ label: provider.provider, monthly: provider.monthlyUsd, top, height: y - top });
      y += PROVIDER_GAP;
    });
    const leftHeight = y;

    const hostById = new Map(hostNodes.map((node) => [node.id, node]));
    services.forEach((service) => {
      service.connected = hostNodes.filter((node) => (node.services || []).includes(service.id)).map((node) => node.id);
    });
    const avgY = (service) => {
      if (!service.connected.length) return Number.MAX_SAFE_INTEGER;
      return service.connected.reduce((sum, id) => sum + hostById.get(id).y, 0) / service.connected.length;
    };
    const svcX = PAD + 12 + HOST_W + EDGE_SPAN;
    let sy = PAD + PROVIDER_HEADER_H;
    const svcNodes = services
      .slice()
      .sort((a, b) => avgY(a) - avgY(b))
      .map((service) => {
        const routeCount = (service.routes || []).length;
        const h = 42 + Math.min(routeCount, 3) * 15;
        const node = { ...service, x: svcX, y: sy, w: SVC_W, h };
        sy += h + SVC_GAP;
        return node;
      });

    const width = svcX + SVC_W + PAD;
    const height = Math.max(leftHeight, sy) + PAD;
    const svg = svgEl("svg", {
      viewBox: `0 0 ${width} ${height}`,
      width,
      height,
      role: "group",
      "aria-label": "Network graph of providers, hosts, VMs, and services",
      class: "pp-graph",
      "data-pp-infra-generated-graph": "true",
    });
    const graphTitle = svgEl("title", {}, svg);
    graphTitle.textContent = "Network graph of providers, hosts, VMs, and services";

    // --- provider containers ---------------------------------------------
    providerBoxes.forEach((box) => {
      const group = svgEl("g", { class: "pp-graph-provider" }, svg);
      svgEl("rect", { x: PAD, y: box.top, width: HOST_W + 32, height: box.height, rx: 10, fill: "var(--color-ghost)", stroke: "var(--color-salt)" }, group);
      const label = svgEl("text", { x: PAD + 12, y: box.top + 19, class: "pp-graph-provider-label" }, group);
      label.textContent = truncate(box.label, 22);
      const monthly = svgEl("text", { x: PAD + HOST_W + 20, y: box.top + 19, "text-anchor": "end", class: "pp-graph-meta" }, group);
      monthly.textContent = money(box.monthly);
    });

    // --- edges ------------------------------------------------------------
    const edgeLayer = svgEl("g", {}, svg);
    const adjacency = new Map();
    const connect = (a, b) => {
      if (!adjacency.has(a)) adjacency.set(a, new Set());
      adjacency.get(a).add(b);
    };
    svcNodes.forEach((service) => {
      const slotCount = Math.max(service.connected.length, 1);
      const slotSpan = Math.min(14, (service.h - 16) / slotCount);
      service.connected.forEach((hostId, index) => {
        const host = hostById.get(hostId);
        const x1 = host.x + host.w;
        const y1 = host.y + host.h / 2;
        const x2 = service.x;
        const y2 = service.y + 12 + index * slotSpan;
        const bend = (x2 - x1) / 2;
        const isPublic = (service.publicAssetNames || []).includes(hostId);
        svgEl("path", {
          d: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
          class: isPublic ? "pp-graph-edge pp-graph-edge-public" : "pp-graph-edge",
          "data-graph-edge": `${hostId}|svc:${service.id}`,
        }, edgeLayer);
        connect(hostId, `svc:${service.id}`);
        connect(`svc:${service.id}`, hostId);
      });
    });
    hostNodes.filter((node) => node.vm).forEach((node) => {
      const parent = hostById.get(node.parent);
      if (!parent) return;
      const elbowX = parent.x + 12;
      svgEl("path", {
        d: `M ${elbowX} ${parent.y + parent.h} V ${node.y + node.h / 2} H ${node.x}`,
        class: "pp-graph-edge pp-graph-edge-containment",
        "data-graph-edge": `${node.parent}|${node.id}`,
      }, edgeLayer);
      connect(node.parent, node.id);
      connect(node.id, node.parent);
    });

    // --- nodes -------------------------------------------------------------
    const nodeKeyFor = (el) => el.getAttribute("data-graph-node");
    const stateFill = (state, historical) => {
      if (historical) return "var(--color-ash)";
      return ["running", "live", "healthy"].includes(String(state)) ? "var(--color-jalapeno)" : "var(--color-saffron)";
    };
    const drawNode = (key, box, lines, href, extraClass = "") => {
      const group = svgEl("g", { class: `pp-graph-node ${extraClass}`.trim(), "data-graph-node": key }, svg);
      const accessibleLabel = lines.map((line) => line.text).join(". ");
      const link = svgEl("a", { href, "aria-label": accessibleLabel }, group);
      svgEl("rect", { x: box.x, y: box.y, width: box.w, height: box.h, rx: 8, class: "pp-graph-node-box" }, link);
      lines.forEach((line, index) => {
        const text = svgEl("text", { x: box.x + (line.dotFill ? 22 : 10), y: box.y + 17 + index * 14, class: line.class }, link);
        text.textContent = line.text;
        if (line.dotFill) svgEl("circle", { cx: box.x + 13, cy: box.y + 13.5, r: 3.5, fill: line.dotFill }, link);
      });
      return group;
    };

    hostNodes.forEach((node) => {
      drawNode(node.id, node, [
        { text: truncate(node.label, node.vm ? 21 : 24), class: "pp-graph-node-label", dotFill: stateFill(node.state, node.historical) },
        { text: truncate(`${node.vm ? "VM" : node.kind} · ${node.monthlyUsd > 0 ? money(node.monthlyUsd) : node.state}`, 30), class: "pp-graph-meta" },
      ], node.href, node.vm ? "pp-graph-node-vm" : "");
    });
    svcNodes.forEach((service) => {
      const lines = [
        { text: truncate(service.label, 26), class: "pp-graph-node-label" },
        { text: `${service.connected.length} host${service.connected.length === 1 ? "" : "s"} · ${money(service.monthlyUsd)}`, class: "pp-graph-meta" },
      ];
      (service.routes || []).slice(0, 3).forEach((route) => {
        lines.push({ text: truncate(route, 30), class: "pp-graph-route" });
      });
      drawNode(`svc:${service.id}`, service, lines, service.href, "pp-graph-node-service");
    });

    // --- pointer and keyboard tracing --------------------------------------
    const allNodes = Array.from(svg.querySelectorAll("[data-graph-node]"));
    const allEdges = Array.from(svg.querySelectorAll("[data-graph-edge]"));
    const clearFocus = () => {
      svg.classList.remove("pp-graph-focus");
      allNodes.forEach((el) => el.classList.remove("is-active"));
      allEdges.forEach((el) => el.classList.remove("is-active"));
    };
    allNodes.forEach((el) => {
      const applyFocus = () => {
        const key = nodeKeyFor(el);
        const linked = adjacency.get(key) || new Set();
        svg.classList.add("pp-graph-focus");
        allNodes.forEach((node) => node.classList.toggle("is-active", nodeKeyFor(node) === key || linked.has(nodeKeyFor(node))));
        allEdges.forEach((edge) => {
          const [from, to] = edge.getAttribute("data-graph-edge").split("|");
          edge.classList.toggle("is-active", from === key || to === key);
        });
      };
      el.addEventListener("mouseenter", applyFocus);
      el.addEventListener("focusin", applyFocus);
      el.addEventListener("mouseleave", () => {
        if (!el.contains(document.activeElement)) clearFocus();
      });
      el.addEventListener("focusout", (event) => {
        if (!el.contains(event.relatedTarget)) clearFocus();
      });
    });

    // --- search dimming (shared with the topology tree search) --------------
    const searchInput = root.closest("[data-pp-infra-map]")?.querySelector("[data-pp-infra-map-search]");
    if (searchInput) {
      const nodeText = new Map();
      hostNodes.forEach((node) => nodeText.set(node.id, `${node.label} ${node.provider} ${node.kind} ${(node.services || []).join(" ")}`.toLowerCase()));
      svcNodes.forEach((service) => nodeText.set(`svc:${service.id}`, `${service.id} ${service.label} ${(service.routes || []).join(" ")}`.toLowerCase()));
      const applySearch = () => {
        const query = searchInput.value.trim().toLowerCase();
        const matched = new Set();
        if (query) {
          nodeText.forEach((text, key) => {
            if (text.includes(query)) matched.add(key);
          });
        }
        allNodes.forEach((el) => {
          const key = nodeKeyFor(el);
          el.classList.toggle("pp-graph-dim", !!query && !matched.has(key));
        });
        allEdges.forEach((edge) => {
          const [from, to] = edge.getAttribute("data-graph-edge").split("|");
          edge.classList.toggle("pp-graph-dim", !!query && !matched.has(from) && !matched.has(to));
        });
      };
      searchInput.addEventListener("input", applySearch);
      root.__infraGraphSearchBinding = { input: searchInput, handler: applySearch };
      applySearch();
    }

    root.appendChild(svg);
  });
}

function initPepperPage() {
  initToasts();
  initCalendars();
  initFlags();
  initLeave();
  initClocks();
  initSun();
  initStandup();
  initInfraQuickJump();
  initInfraFilterableTables();
  initScrollableTables();
  initInfraMap();
  initInfraGraph();
}

bindPepperUI();

document.addEventListener("turbo:load", () => {
  closeAllDropdowns();
  closeAllModals();
  setSidebar(false);
  initPepperPage();
});

document.addEventListener("turbo:before-cache", resetInfraGraphs);

setSidebar(false);
initThemeFromStorage();
initPepperPage();
